import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Table2, FileText, ScrollText, LogOut, Activity, ShieldCheck, Upload, Sun, Moon, Users, Contact, LineChart, ClipboardCheck, Trash2, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { roleLabel } from "@/lib/format";
import IdleGuard from "@/components/IdleGuard";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/analytics", label: "Executive Analytics", icon: LineChart, testid: "nav-analytics" },
  { to: "/referrals", label: "Referrals", icon: Table2, testid: "nav-referrals" },
  { to: "/report", label: "Pre-IDT Report", icon: FileText, testid: "nav-report" },
  { to: "/import", label: "Import Workbook", icon: Upload, testid: "nav-import", roles: ["coordinator", "manager", "admin"] },
  { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit", roles: ["coordinator", "manager", "admin"] },
  { to: "/deleted", label: "Recently Deleted", icon: Trash2, testid: "nav-deleted", roles: ["admin"] },
  { to: "/users", label: "Users", icon: Users, testid: "nav-users", superOnly: true },
  { to: "/access-review", label: "Access Review", icon: ClipboardCheck, testid: "nav-access-review", superOnly: true },
  { to: "/contacts", label: "Contacts", icon: Contact, testid: "nav-contacts", roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="app-bg min-h-screen flex text-foreground">
      <IdleGuard />
      <aside className="w-[236px] shrink-0 glass border-r border-white/8 flex flex-col fixed h-screen z-20 no-print">
        <div className="px-5 pt-6 pb-5 border-b border-white/8">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-cyan-400" />
            <span className="font-head font-black text-lg tracking-tight">GLH<span className="text-cyan-400">·</span>IDT</span>
          </div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mt-1.5 font-mono">Referral Tracker</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter((n) => (!n.roles || n.roles.includes(user?.role)) && (!n.superOnly || user?.is_super_admin)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-cyan-500/12 text-cyan-300 border border-cyan-500/30"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 pb-4">
          <div className="glass rounded-lg px-3 py-2.5 mb-2 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="text-[11px] leading-tight text-muted-foreground font-mono">PHI-safe · deterministic engine</span>
          </div>
          <div className="px-2 py-1">
            <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-xs text-cyan-400/80 font-mono">{roleLabel(user?.role)}</div>
          </div>
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors"
          >
            {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            {theme === "light" ? "Dark theme" : "Light theme"}
          </button>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-red-300 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-[236px] print:ml-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
